/**
 * UserFormPage - Página de criação/edição de usuário
 *
 * Seções:
 * 1. Informações Básicas (nome, email, telefone)
 * 2. Segurança (senha, 2FA)
 * 3. Permissões de Acesso
 * 4. Preferências
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser, useCreateUser, useUpdateUser } from '../hooks';
import { Button } from '@/shared/components/ui/Button';
import { Breadcrumb } from '@/shared/components/ui';
import type { CreateUserRequest, UpdateUserRequest } from '@troia-v3/types';

// Sections
import { BasicUserInfoSection } from '../components/sections/BasicUserInfoSection';
import { UserSecuritySection } from '../components/sections/UserSecuritySection';
import { UserPermissionsSection } from '../components/sections/UserPermissionsSection';
import { Icon } from '@/shared/icons'
export const UserFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  // Fetch user data (only in edit mode)
  const { data: user, isLoading, isError } = useUser(id!, { enabled: isEditMode });
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  // Form state
  const [formData, setFormData] = useState<Partial<CreateUserRequest & UpdateUserRequest>>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    code: '',
    document: '',
    password: '',
    sendInvite: true,
    levelId: undefined,
  });
  const [hasChanges, setHasChanges] = useState(!isEditMode); // New users always have changes

  // Update form data when user loads (edit mode)
  React.useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone || '',
        code: user.code || '',
        document: user.document || '',
        levelId: user.levelId,
      });
      setHasChanges(false);
    }
  }, [user]);

  // Handle form field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Validation
  const validateForm = (): string | null => {
    if (!formData.email?.trim()) return 'Email é obrigatório';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Email inválido';
    if (!formData.firstName?.trim()) return 'Nome é obrigatório';
    if (formData.firstName.trim().length < 2) return 'Nome deve ter pelo menos 2 caracteres';
    if (!formData.lastName?.trim()) return 'Sobrenome é obrigatório';
    if (formData.lastName.trim().length < 2) return 'Sobrenome deve ter pelo menos 2 caracteres';

    // Password validation (only for create mode or if password is provided in edit mode)
    if (!isEditMode && !formData.sendInvite) {
      if (!formData.password) return 'Senha é obrigatória';
      if (formData.password.length < 6) return 'Senha deve ter pelo menos 6 caracteres';
    }

    return null;
  };

  // FiSave changes
  const handleSave = async () => {
    if (!hasChanges) return;

    const validationError = validateForm();
    if (validationError) {
      // TODO: Show error toast
      alert(validationError);
      return;
    }

    try {
      if (isEditMode) {
        // Update existing user
        const updateData: UpdateUserRequest = {
          firstName: formData.firstName!,
          lastName: formData.lastName!,
          phone: formData.phone || undefined,
          // `null` = LIMPAR. Com `undefined` o backend entende "não mexer" e o
          // valor antigo sobreviveria enquanto a tela diz "salvo com sucesso".
          code: formData.code ? formData.code : null,
          document: formData.document ? formData.document : null,
          levelId: formData.levelId,
          // Include password if provided (will be hashed on backend)
          ...(formData.password && { password: formData.password }),
        };

        await updateMutation.mutateAsync({ id, data: updateData });

        setHasChanges(false);
      } else {
        // Create new user
        const createData: CreateUserRequest = {
          email: formData.email!,
          firstName: formData.firstName!,
          lastName: formData.lastName!,
          phone: formData.phone || undefined,
          code: formData.code || undefined,
          document: formData.document || undefined,
          password: formData.sendInvite ? undefined : formData.password,
          sendInvite: formData.sendInvite,
          levelId: formData.levelId,
        };

        const newUser = await createMutation.mutateAsync(createData);

        // Navigate to edit page of the newly created user
        navigate(`/settings/users/${newUser.id}/edit`);
      }
    } catch (error: any) {
      // 403 errors are handled by global interceptor (toast)
      if (error.response?.status === 403) return;
      if (error.response?.status === 402) return;
    }
  };

  // Loading state (only for edit mode)
  if (isEditMode && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: 'var(--theme-button-primary)' }} />
      </div>
    );
  }

  // Error state (only for edit mode)
  if (isEditMode && (isError || !user)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-lg" style={{ color: 'var(--theme-error-color)' }}>
          Erro ao carregar usuário
        </p>
        <Button onClick={() => window.location.reload()} variant="secondary">
          Tentar Novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderBottom: '1px solid var(--theme-card-border)',
        }}
      >
        <div className="px-4 h-14 flex flex-col justify-center">
          <div className="flex items-center justify-between">
            {/* Breadcrumb (F25-31: shared — mobile = legenda condensada sob o título, NUNCA seta voltar) */}
            <Breadcrumb
              items={[
                { label: 'Configurações', onNavigate: () => navigate('/settings'), testid: 'users-form-breadcrumb-settings' },
                { label: 'Usuários', onNavigate: () => navigate('/settings/users'), testid: 'users-form-breadcrumb' },
                { label: isEditMode && user ? `${user.firstName} ${user.lastName}` : 'Novo usuário', testid: 'users-form-breadcrumb-current' },
              ]}
              mobileLegend="Configurações · Usuário"
              data-testid="users-form-breadcrumb-nav"
            />

            <div className="flex items-center gap-3">
              {hasChanges && (
                <span
                  className="text-sm hidden sm:inline"
                  style={{ color: 'var(--theme-warning-color)' }}
                >
                  {isEditMode ? 'Alterações não salvas' : 'Preencha os campos obrigatórios'}
                </span>
              )}
              <Button
                variant="secondary"
                className="hidden sm:inline-flex"
                onClick={() => navigate('/settings/users')}
                data-testid="users-form-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <>
                    <Icon name="loader" className="w-4 h-4 mr-2 animate-spin" />
                    {isEditMode ? 'Salvando...' : 'Criando...'}
                  </>
                ) : (
                  <>
                    <Icon name="save" className="w-4 h-4 mr-2" />
                    {isEditMode ? 'Salvar Alterações' : 'Criar Usuário'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        <div className="space-y-6">
          {/* 1. Basic Info Section */}
          <BasicUserInfoSection
            formData={formData}
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />

          {/* 2. Security Section */}
          <UserSecuritySection
            formData={formData}
            isEditMode={isEditMode}
            onFieldChange={handleFieldChange}
          />

          {/* 3. Permissions Section */}
          <UserPermissionsSection
            levelId={formData.levelId}
            onLevelChange={(levelId) => handleFieldChange('levelId', levelId)}
          />
        </div>
      </div>
    </div>
  );
};

UserFormPage.displayName = 'UserFormPage';
